import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  buildGiftDrinkItems, buildGiftDrinkOptions, getGiftDrinkAllowance,
  getPromotionPeriod, isPromotionAvailable, reconcileGiftDrinks, sumGiftDrinks,
} from "../utils/orderGiftDrinks";

// Page-lifetime cache: a browser reload starts fresh. Shared promises also avoid
// duplicate requests when React StrictMode remounts the order page.
const catalogRequests = new Map();
const loadCatalog = (apiBase, storeId, scope) => {
  const key = `${apiBase}:${scope}`;
  if (!catalogRequests.has(key)) {
    catalogRequests.set(key, (async () => {
      if (!storeId) return { scope, promotions: [], options: [], error: "Không tìm thấy thông tin cửa hàng" };
      const [promotionResult, menuResult] = await Promise.allSettled([
        axios.get(`${apiBase}/promotions/${encodeURIComponent(storeId)}`, { timeout: 15000 }),
        axios.get(`${apiBase}/promotion-details/9`, { timeout: 15000 }),
      ]);
      let promotions = [], options = [];
      const errors = [];
      if (promotionResult.status === "fulfilled" && Array.isArray(promotionResult.value.data)) {
        promotions = promotionResult.value.data.map((promotion) => ({ ...promotion, period: getPromotionPeriod(promotion) }));
      } else {
        errors.push("Không tải được ưu đãi của cửa hàng");
      }
      try {
        if (menuResult.status !== "fulfilled") throw new Error("Không tải được menu nước tặng mã 9");
        // Both allowances use the same drink menu of promotion 9.
        options = buildGiftDrinkOptions([{ promotionId: 9, rows: menuResult.value.data }])
          .map((option) => ({ ...option, promotionIds: [9, 13] }));
      } catch (error) {
        errors.push(error.message);
      }
      return { scope, promotions, options, error: errors.length ? `${errors.join(". ")}. Tải lại trang để thử lại.` : "" };
    })());
  }
  return catalogRequests.get(key);
};

export default function useOrderGiftDrinks({ apiBase, storeId, storeScope, items }) {
  const scope = `${storeScope || ""}:${storeId || ""}`;
  const [catalog, setCatalog] = useState({ scope: "", promotions: [], options: [], error: "" });
  const [availability, setAvailability] = useState({ scope: "", promotions: [] });
  const [selections, setSelections] = useState({});
  const [selectionError, setSelectionError] = useState("");
  const loading = catalog.scope !== scope;
  const options = useMemo(() => catalog.scope === scope ? catalog.options : [], [catalog, scope]);
  const availablePromotions = useMemo(() => availability.scope === scope ? availability.promotions : [], [availability, scope]);
  const allowance = useMemo(() => getGiftDrinkAllowance(items, availablePromotions), [items, availablePromotions]);
  const selected = useMemo(() => reconcileGiftDrinks(selections, options, allowance), [selections, options, allowance]);

  useEffect(() => {
    let current = true;
    setSelections({});
    setSelectionError("");
    loadCatalog(apiBase, storeId, scope).then((next) => {
      if (current) setCatalog(next);
    });
    return () => { current = false; };
  }, [apiBase, storeId, scope]);

  useEffect(() => {
    if (catalog.scope !== scope) return;
    const checkTime = () => {
      const promotions = catalog.promotions.filter((promotion) => isPromotionAvailable(promotion));
      // Rerender the order only when eligibility changes, not every clock tick.
      setAvailability((previous) => previous.scope === scope && previous.promotions.length === promotions.length &&
        previous.promotions.every((promotion, index) => promotion === promotions[index]) ? previous : { scope, promotions });
    };
    checkTime();
    const timer = window.setInterval(checkTime, 1000);
    window.addEventListener("focus", checkTime);
    document.addEventListener("visibilitychange", checkTime);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", checkTime);
      document.removeEventListener("visibilitychange", checkTime);
    };
  }, [catalog, scope]);

  useEffect(() => {
    // Persist reductions so adding cakes later never restores old gifts.
    setSelections((previous) => JSON.stringify(previous) === JSON.stringify(selected) ? previous : selected);
  }, [selected]);

  const changeQuantity = (key, value) => {
    if (loading) return;
    const quantity = Math.max(0, Math.floor(Number(value) || 0));
    if (!Number.isFinite(quantity)) return;
    const candidate = { ...selected, [key]: quantity };
    if (!quantity) delete candidate[key];
    const normalized = reconcileGiftDrinks(candidate, options, getGiftDrinkAllowance(items, availablePromotions));
    if (sumGiftDrinks(normalized) === sumGiftDrinks(candidate)) {
      setSelectionError("");
      setSelections(normalized);
    }
  };

  const validateForCheckout = async () => {
    if (!sumGiftDrinks(selected)) return [];
    // Recheck the cached time window immediately, including after a sleeping tab.
    const next = reconcileGiftDrinks(selected, options, getGiftDrinkAllowance(items, availablePromotions));
    if (sumGiftDrinks(next) !== sumGiftDrinks(selected)) {
      setSelections(next);
      setSelectionError("Ưu đãi đã hết thời gian áp dụng. Vui lòng kiểm tra lại nước tặng.");
      return null;
    }
    return buildGiftDrinkItems(next, options);
  };

  return {
    options, allowance, selected, availablePromotions, loading,
    error: (catalog.scope === scope ? catalog.error : "") || selectionError,
    changeQuantity, validateForCheckout,
    quantity: sumGiftDrinks(selected), limit: allowance[9] + allowance[13],
    giftItems: buildGiftDrinkItems(selected, options),
    clear: () => { setSelections({}); setSelectionError(""); },
  };
}
